import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Checkbox,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { FaFileAudio } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import { useState, useEffect } from 'react';

interface SharedFile {
  id: string;
  name: string;
  size: number;
  url: string;
}

interface SelectiveFileLoadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadSelected: (selectedFileIds: string[]) => void;
  onLoadAll: () => void;
  files: SharedFile[];
  totalSizeMB: number;
}

const SelectiveFileLoadingModal = ({
  isOpen,
  onClose,
  onLoadSelected,
  onLoadAll,
  files,
  totalSizeMB
}: SelectiveFileLoadingModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [fileDetails, setFileDetails] = useState<Map<string, { size: number }>>(new Map());
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Select all files by default and fetch file details
  useEffect(() => {
    if (isOpen) {
      setSelectedFiles(new Set(files.map(file => file.id)));
      fetchFileDetails();
    }
  }, [isOpen, files]);

  const fetchFileDetails = async () => {
    setIsLoadingDetails(true);
    const details = new Map<string, { size: number }>();
    
    // Helper function to try fetching file size with multiple strategies
    const fetchFileSize = async (url: string, fileId: string): Promise<number> => {
      // Strategy 1: Try HEAD request (works for servers that support it)
      try {
        const response = await fetch(url, { method: 'HEAD' });
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const size = parseInt(contentLength, 10);
          if (size > 0) return size;
        }
      } catch (error) {
        // Continue to next strategy
      }

      // Strategy 2: Try Range request to get Content-Range header
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Range': 'bytes=0-1'
          }
        });
        
        if (response.status === 206) {
          // Partial content response - extract size from Content-Range header
          const contentRange = response.headers.get('content-range');
          if (contentRange) {
            const match = contentRange.match(/\/(\d+)$/);
            if (match) {
              const size = parseInt(match[1], 10);
              if (size > 0) return size;
            }
          }
        } else if (response.ok) {
          // Full response - check content-length
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const size = parseInt(contentLength, 10);
            if (size > 0) return size;
          }
        }
      } catch (error) {
        // Continue to next strategy
      }

      // Strategy 3: Try CORS proxy (useful for direct file links with CORS restrictions)
      try {
        const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
        const response = await fetch(proxyUrl, { method: 'HEAD' });
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const size = parseInt(contentLength, 10);
          if (size > 0) return size;
        }
      } catch (error) {
        // Continue to next strategy
      }

      // Strategy 4: Try Range request through CORS proxy
      try {
        const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: {
            'Range': 'bytes=0-1'
          }
        });
        
        if (response.status === 206) {
          const contentRange = response.headers.get('content-range');
          if (contentRange) {
            const match = contentRange.match(/\/(\d+)$/);
            if (match) {
              const size = parseInt(match[1], 10);
              if (size > 0) return size;
            }
          }
        }
      } catch (error) {
        // All strategies failed
      }

      return 0;
    };
    
    try {
      await Promise.all(files.map(async (file) => {
        try {
          // If file already has a size set, use it (but still try to fetch if it's 0)
          let size = file.size && file.size > 0 ? file.size : 0;
          
          // Only fetch if size is not already known
          if (size === 0) {
            size = await fetchFileSize(file.url, file.id);
          }
          
          details.set(file.id, { size });
          if (size === 0) {
            console.warn(`Could not fetch size for file ${file.id} (${file.name})`);
          }
        } catch (error) {
          console.warn(`Could not fetch details for file ${file.id}:`, error);
          // Fallback to file.size if available, otherwise 0
          details.set(file.id, { size: file.size && file.size > 0 ? file.size : 0 });
        }
      }));
    } catch (error) {
      console.error('Error fetching file details:', error);
    }
    
    setFileDetails(details);
    setIsLoadingDetails(false);
  };

  const handleFileToggle = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };


  const handleLoadSelected = () => {
    onLoadSelected(Array.from(selectedFiles));
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };


  const selectedSizeMB = Array.from(selectedFiles).reduce((total, fileId) => {
    const details = fileDetails.get(fileId);
    return total + (details ? details.size / (1024 * 1024) : 0);
  }, 0);

  const selectedCount = selectedFiles.size;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }} isCentered>
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent bg="rgba(20, 20, 30, 0.95)" borderRadius="xl" p={4} mx={4} maxH="90vh" maxW={{ base: "100%", md: "600px" }}>
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Icon as={FaFileAudio} color="blue.400" />
            <Text fontSize="xl" fontWeight="bold">
              Select Files to Load
            </Text>
          </HStack>
        </ModalHeader>

        <ModalBody py={4} overflowY="auto">
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md" bg="blue.900" border="1px solid" borderColor="blue.600">
              <AlertIcon color="blue.300" />
              <AlertDescription fontSize="sm" color="blue.100">
                This is a large setup. You can select specific files or load them all at once. You can always click the{' '}
                <Icon as={FiRefreshCw} display="inline" verticalAlign="middle" mx={1} /> button to return to this page and choose different files.
              </AlertDescription>
            </Alert>

            <HStack spacing={4} justify="flex-end" align="center">
              <Text fontSize="sm" color="gray.300">
                {selectedCount}/{files.length} files selected ({isLoadingDetails ? 'Loading...' : `~${selectedSizeMB.toFixed(0)}MB`})
              </Text>
            </HStack>

            <Divider />

            <Box maxH="400px" overflowY="auto" border="1px solid" borderColor="gray.600" borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="gray.800" zIndex={1}>
                  <Tr>
                    <Th width="50px">
                      <Checkbox
                        isChecked={selectedFiles.size === files.length && files.length > 0}
                        isIndeterminate={selectedFiles.size > 0 && selectedFiles.size < files.length}
                        onChange={() => {
                          if (selectedFiles.size === files.length) {
                            setSelectedFiles(new Set());
                          } else {
                            setSelectedFiles(new Set(files.map(file => file.id)));
                          }
                        }}
                        colorScheme="blue"
                      />
                    </Th>
                    <Th>File Name</Th>
                    <Th width="100px">Size</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {files.map((file) => {
                    const details = fileDetails.get(file.id);
                    return (
                      <Tr key={file.id} _hover={{ bg: 'gray.700' }}>
                        <Td>
                          <Checkbox
                            isChecked={selectedFiles.has(file.id)}
                            onChange={() => handleFileToggle(file.id)}
                            colorScheme="blue"
                            isDisabled={isLoadingDetails}
                          />
                        </Td>
                        <Td>
                          <Text fontSize="sm" noOfLines={1} title={file.name}>
                            {file.name}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="gray.300">
                            {isLoadingDetails ? 'Loading...' : formatFileSize(details?.size || 0)}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter pt={4}>
          <HStack spacing={3} width="100%">
            <Button 
              variant="outline" 
              onClick={onClose}
              flex={1}
              size="md"
            >
              Cancel
            </Button>
            <Button 
              colorScheme="blue"
              onClick={handleLoadSelected}
              isDisabled={selectedCount === 0 || isLoadingDetails}
              flex={1}
              size="md"
            >
              Load
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SelectiveFileLoadingModal;
